import { useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { Transaction } from '@App/redux/slices/reports/ReportsSlice'

import {Order, Trade} from "@Redux/slices/trade/trades/types"

import { replaceVarsInPath, ROUTE_NAMES } from '@UI/crypto_exchange/desktop/routes/routeNames'

import { useOnSuccessfulTrade } from '../../hooks/useOnSuccessfulTrade'

import { Analytics } from './components/Analytics'
import { EditOrderContainer } from './components/EditOrder'
import { EditTradeContainer } from './components/EditTrade'
import { Header } from './components/Header'
import { Filters } from './components/Header/components/Filters'
import { SendReportButton } from './components/Header/components/SendReportButton'
import { Tabs } from './components/Header/components/Tabs'
import { OrdersTable } from './components/OrdersTable'
import { SideBar } from './components/SideBar/Index'
import { TradeInfo } from './components/TradeInfo'
import { TradesTable } from './components/TradesTable'
import { TransactionInfo } from './components/TransactionInfo'
import { TransactionsTable } from './components/TransactionsTable'
import { REPORT_STATUS_OPTIONS, REPORT_TYPE_OPTIONS, REPORTS_TABS, ReportsFilter, ReportsTabs } from './model'
import s from './styles.module.scss'

enum SideBarTypes {
  Transactions = 1,
  Trades = 2,
  Orders = 3,
  TradesEdit = 4,
  OrdersEdit = 5,
}

export const Reports = () => {
  const { tab } = useParams<{ tab: ReportsTabs }>()
  const activeTab = tab ?? ReportsTabs.Transactions
  const navigate = useNavigate()
  const [openedSideBar, setOpenedSideBar] = useState<{
    title: string
    type: SideBarTypes
    data: Trade | Order | Transaction
  } | null>(null)
  const handleTradesRowClick = (trade: Trade) => {
    setOpenedSideBar({ title: `Trade ${trade.status}`, type: SideBarTypes.Trades, data: trade })
  }
  const handleOrdersRowClick = (trade: Order) => {
    setOpenedSideBar({ title: `Order ${trade.status}`, type: SideBarTypes.Orders, data: trade })
  }
  const handleTransactionRowClick = (transaction: Transaction) => {
    setOpenedSideBar({ title: `BTC/EUR ${transaction.type}`, type: SideBarTypes.Transactions, data: transaction })
  }
  const handleEditTrade = () => {
    setOpenedSideBar((prev) => {
      if (!prev?.data) {
        return null
      }
      return { data: prev.data, title: 'Edit trade', type: SideBarTypes.TradesEdit }
    })
  }
  const handleEditOrder = () => {
    setOpenedSideBar((prev) => {
      if (!prev?.data) {
        return null
      }
      return { data: prev.data, title: 'Edit order', type: SideBarTypes.OrdersEdit }
    })
  }
  const setTab = (tab: ReportsTabs) => {
    setOpenedSideBar(null)
    navigate(replaceVarsInPath(ROUTE_NAMES.REPORTS, [tab]))
  }
  const activeTabOptions = REPORTS_TABS.find((v) => v.value === activeTab)
  const [filters, setFilters] = useState<ReportsFilter>(() => ({
    type: [...REPORT_TYPE_OPTIONS.map((v) => v.value)],
    period: '1m',
    status: [...REPORT_STATUS_OPTIONS.map((v) => v.value)],
  }))
  const setFilter = <K extends keyof ReportsFilter>(key: K, value: ReportsFilter[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }
  useOnSuccessfulTrade(() => {
    setOpenedSideBar(null)
  })

  return (
    <div className={s.container}>
      <Header
        tabsSelector={<Tabs activeTab={activeTab} setActiveTab={setTab} />}
        filters={
          <Filters
            filters={filters}
            setFilter={setFilter}
            enabledFilters={activeTabOptions?.filters}
            reportButton={activeTabOptions?.showSendReport && <SendReportButton />}
          />
        }
      />
      <div className={s.content}>
        {activeTab === ReportsTabs.Transactions && <TransactionsTable onRowClick={handleTransactionRowClick} />}
        {activeTab === ReportsTabs.Trades && <TradesTable onRowClick={handleTradesRowClick} />}
        {activeTab === ReportsTabs.Orders && <OrdersTable onRowClick={handleOrdersRowClick} />}
        {activeTab === ReportsTabs.Analytics && <Analytics />}
        {openedSideBar && (
          <SideBar headerContent={openedSideBar.title} onClose={() => setOpenedSideBar(null)}>
            {openedSideBar.type === SideBarTypes.Trades && (
              <TradeInfo trade={openedSideBar.data as Trade} onEditTrade={handleEditTrade} />
            )}
            {openedSideBar.type === SideBarTypes.Orders && (
              <TradeInfo trade={openedSideBar.data as Order} onEditTrade={handleEditOrder} type="order" />
            )}
            {openedSideBar.type === SideBarTypes.TradesEdit && (
              <EditTradeContainer order={openedSideBar.data as Trade} />
            )}
            {openedSideBar.type === SideBarTypes.OrdersEdit && (
              <EditOrderContainer order={openedSideBar.data as Order} />
            )}
            {openedSideBar.type === SideBarTypes.Transactions && (
              <TransactionInfo transaction={openedSideBar.data as Transaction} />
            )}
          </SideBar>
        )}
      </div>
    </div>
  )
}
